import axios from 'axios';
import { Image } from '../types';
import { PricePackage } from '../context/PriceContext';

// URLs relativas para usar con el proxy
const API_URL = '/canvamanager';
const IMAGES_API_URL = `${API_URL}/imgs/preview`;
const PRICE_API_URL = `${API_URL}/imgs/paquetes/precios`;

// Precio individual por defecto en caso de error
const DEFAULT_INDIVIDUAL_PRICE = 2.5;

// Función para procesar los datos de imagen del servidor
const processImageData = (images: any[], price: number): Image[] => {
  console.log('Processing images data:', images);
  
  if (!Array.isArray(images) || images.length === 0) {
    console.warn('No images to process');
    return [];
  }
  
  return images.map((img, index) => {
    if (!img || typeof img !== 'object' || !img.id || !img.url) {
      console.warn('Invalid image data:', img);
      return null;
    }
    
    // Modificar URL para usar el proxy si es una URL absoluta
    let imgUrl = img.url;
    if (imgUrl.startsWith('https://canvasoft.com:8487/')) {
      imgUrl = imgUrl.replace('https://canvasoft.com:8487/', '/');
    }
    
    return {
      id: img.id.toString(),
      url: imgUrl,
      title: img.title || `Imagen ${img.id}`,
      description: img.description || `Imagen generada #${img.id}`,
      width: img.width || 800,
      height: img.height || 800,
      price: price,
      tags: img.tags || ''
    };
  }).filter(img => img !== null) as Image[];
};

// Función para simular retraso en la respuesta de la API
const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

// Función para obtener los precios desde el webservice
const getPricePackages = async (): Promise<PricePackage[] | null> => {
  try {
    const response = await axios.get(PRICE_API_URL);
    return response.data;
  } catch (error) {
    console.error('Error fetching price packages:', error);
    return null;
  }
};

export const api = {
  // Obtener paquetes de precios
  getPricePackages,
  
  // Obtener imagen por ID
  getImageById: async (id: string, price = DEFAULT_INDIVIDUAL_PRICE): Promise<Image | null> => {
    try {
      const url = `${IMAGES_API_URL}/fileid/${id}`;
      console.log('Fetching image by ID from:', url);
      const response = await axios.get(url);
      console.log('Image by ID response data:', response.data);
      
      if (!response.data || typeof response.data !== 'object') {
        console.error('Expected object but got:', typeof response.data);
        return null;
      }
      
      const processedImages = processImageData([response.data], price);
      return processedImages.length > 0 ? processedImages[0] : null;
    } catch (error) {
      console.error('Error fetching image by ID:', error);
      return null;
    }
  },
  
  // Búsqueda de imágenes
  searchImages: async (query: string, page = 0, size = 40, price = DEFAULT_INDIVIDUAL_PRICE): Promise<Image[]> => {
    try {
      // Construir la URL con los parámetros de búsqueda
      let url = IMAGES_API_URL;
      if (query) {
        url += `?q=${encodeURIComponent(query)}&page=${page}&size=${size}`;
      } else {
        url += `?page=${page}&size=${size}`;
      }
      
      console.log('Fetching images from:', url);
      const response = await axios.get(url);
      console.log('Response data:', response.data);
      
      if (!Array.isArray(response.data)) {
        console.error('Expected array but got:', typeof response.data);
        return [];
      }
      
      return processImageData(response.data, price);
    } catch (error) {
      console.error('Error searching images:', error);
      return [];
    }
  },

  // Obtener imágenes destacadas o populares (ahora usa el mismo endpoint)
  getFeaturedImages: async (page = 0, size = 40, price = DEFAULT_INDIVIDUAL_PRICE, seed = 0): Promise<Image[]> => {
    try {
      const url = seed > 0
        ? `${IMAGES_API_URL}?page=${page}&size=${size}&seed=${seed}`
        : `${IMAGES_API_URL}?page=${page}&size=${size}`;
      console.log('Fetching featured images from:', url);
      const response = await axios.get(url);
      console.log('Featured response data:', response.data);
      
      if (!Array.isArray(response.data)) {
        console.error('Expected array but got:', typeof response.data);
        return [];
      }
      
      return processImageData(response.data, price);
    } catch (error) {
      console.error('Error fetching featured images:', error);
      return [];
    }
  },

  // Procesar compra (simulado)
  processPayment: async (cartItems: Image[], totalAmount: number): Promise<boolean> => {
    try {
      // En un entorno real:
      // const response = await axios.post(`${API_URL}/payments`, { cartItems, totalAmount });
      // return response.data.success;
      
      await delay(1000);
      console.log('Processing payment for', cartItems.length, 'items, total:', totalAmount);
      return true; // Simular éxito
    } catch (error) {
      console.error('Error processing payment:', error);
      return false;
    }
  },

  // Borrar imagen (solo para admins)
  delete: async (url: string, config?: any): Promise<any> => {
    try {
      return await axios.delete(url, config);
    } catch (error) {
      console.error('Error deleting:', error);
      throw error;
    }
  }
};